import { useState } from 'react';
import { AuthImage } from '@/components/auth-image';
import { ImageLightbox } from '@/components/image-lightbox';
import { cn } from '@/lib/utils';
import type { CatalogImage } from '@/features/catalog-image/types';

/**
 * Read-only photo strip for a catalog entity (menu item / offering / service).
 * Tap any thumbnail to open the fullscreen lightbox. Renders nothing when empty.
 *
 * - `cover`: a single 16/9 hero (first image) + a "+N" overlay if there are more.
 * - default: a horizontal strip of square thumbnails.
 */
export function ImageCarousel({
  images,
  cover = false,
  size = 'h-16 w-16',
  className,
}: {
  images: CatalogImage[];
  cover?: boolean;
  size?: string;
  className?: string;
}) {
  const [open_at, set_open_at] = useState<number | null>(null);
  if (!images || images.length === 0) return null;
  const full_paths = images.map((i) => i.full_path);

  const lightbox =
    open_at !== null ? (
      <ImageLightbox paths={full_paths} index={open_at} on_close={() => set_open_at(null)} on_navigate={set_open_at} />
    ) : null;

  if (cover) {
    const extra = images.length - 1;
    return (
      <>
        <button
          type="button"
          onClick={() => set_open_at(0)}
          className={cn('relative block aspect-[16/9] w-full overflow-hidden rounded-card border-2 border-brand-outline-variant/15 bg-muted shadow-clay-sm', className)}
          aria-label="View photos"
          data-testid="carousel-cover"
        >
          <AuthImage path={`${images[0].thumb_path}`} alt="" className="h-full w-full object-cover" />
          {extra > 0 && (
            <span className="absolute bottom-1.5 right-1.5 rounded-full bg-foreground/70 px-2 py-0.5 text-xs font-semibold text-white">+{extra}</span>
          )}
        </button>
        {lightbox}
      </>
    );
  }

  return (
    <>
      <div className={cn('flex gap-2 overflow-x-auto pb-1', className)} data-testid="carousel-strip">
        {images.map((img, i) => (
          <button
            key={img.id}
            type="button"
            onClick={() => set_open_at(i)}
            className={cn('shrink-0 overflow-hidden rounded-input border-2 border-brand-outline-variant/15 bg-muted transition-opacity hover:opacity-85', size)}
            aria-label="View photo"
            data-testid={`carousel-thumb-${img.id}`}
          >
            <AuthImage path={img.thumb_path} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
      {lightbox}
    </>
  );
}
